(() => {
  const prefix = 'vv_digital_consent_';
  const keys = ['accepted', 'wording', 'version', 'cart', 'items', 'time'];
  const root = window.Shopify?.routes?.root || '/';
  let record = null;
  let revision = 0;
  let queue = Promise.resolve();
  let clearing = null;
  let attributes = JSON.parse(document.getElementById('velvele-cart-state').textContent).attributes || {};
  let submitting = false;
  const controls = () => ({
    panel: document.querySelector('[data-digital-consent]'),
    checkbox: document.getElementById('DigitalDeliveryConsent'),
    button: document.querySelector('#main-cart-footer [name="checkout"]')
  });
  const fingerprint = cart => JSON.stringify({
    currency: cart.currency,
    total: cart.total_price,
    items: cart.items.filter(item => item.quantity > 0).map(item => [item.key, item.variant_id, item.quantity, item.final_line_price]).sort((a, b) => String(a[0]).localeCompare(String(b[0])))
  });
  const lock = () => {
    const {checkbox, button} = controls();
    if (checkbox) checkbox.checked = false;
    if (button) button.disabled = false;
  };
  const message = failed => {
    const {panel, checkbox} = controls();
    panel?.toggleAttribute('data-consent-invalid', failed);
    if (failed) checkbox?.setAttribute('aria-invalid', 'true');
    else checkbox?.removeAttribute('aria-invalid');
    const status = panel?.querySelector('[role="status"]');
    if (status) status.textContent = failed ? panel.dataset.error : '';
  };
  const requireConsent = () => {
    lock();
    const {panel, checkbox} = controls();
    panel?.setAttribute('data-consent-invalid', '');
    checkbox?.setAttribute('aria-invalid', 'true');
    const status = panel?.querySelector('[role="status"]');
    if (status) status.textContent = panel.dataset.required;
    checkbox?.focus({preventScroll: true});
    panel?.querySelector('.vv-digital-consent')?.scrollIntoView({block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
  };
  const readCart = async () => {
    const response = await fetch(root + 'cart.js', {credentials: 'same-origin', cache: 'no-store'});
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  };
  const write = values => {
    const task = queue.catch(() => {}).then(async () => {
      const response = await fetch(root + 'cart/update.js', {
        method: 'POST', credentials: 'same-origin',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({attributes: values})
      });
      if (!response.ok) throw new Error('Consent not saved');
      const cart = await response.json();
      attributes = cart.attributes || {};
      return cart;
    });
    queue = task;
    return task;
  };
  const invalidate = (force = false) => {
    revision++;
    record = null;
    lock();
    if (!force && !keys.some(key => attributes[prefix + key])) return Promise.resolve();
    if (clearing) return clearing;
    clearing = write(Object.fromEntries(keys.map(key => [prefix + key, '']))).catch(() => message(true)).finally(() => { clearing = null; });
    return clearing;
  };
  document.addEventListener('change', async event => {
    if (event.target.id !== 'DigitalDeliveryConsent') return;
    const {checkbox, button, panel} = controls();
    if (!checkbox.checked) { message(false); invalidate(true); return; }
    const version = ++revision;
    record = null;
    button.disabled = true;
    message(false);
    try {
      await queue.catch(() => {});
      const cart = await readCart();
      if (!cart.item_count || version !== revision || !checkbox.checked) return;
      const signature = fingerprint(cart);
      const values = {
        [prefix + 'accepted']: 'yes',
        [prefix + 'wording']: panel.querySelector('.field__label').textContent.trim(),
        [prefix + 'version']: panel.dataset.version,
        [prefix + 'cart']: signature,
        [prefix + 'items']: JSON.stringify(cart.items.filter(item => item.quantity > 0).map(item => ({product: item.product_id, variant: item.variant_id, title: item.title, quantity: item.quantity, amount: item.final_line_price}))),
        [prefix + 'time']: new Date().toISOString()
      };
      const saved = await write(values);
      if (version !== revision || !checkbox.checked || signature !== fingerprint(saved)) { await invalidate(true); return; }
      if (!Object.entries(values).every(([key, value]) => saved.attributes?.[key] === value)) throw new Error('Consent readback failed');
      record = {signature, values};
      button.disabled = false;
    } catch {
      await invalidate(true);
      message(true);
    }
  });
  document.addEventListener('submit', async event => {
    if (event.target.id !== 'cart' || submitting) return;
    if (event.submitter && event.submitter.name !== 'checkout') return;
    event.preventDefault();
    const {checkbox, button} = controls();
    if (!checkbox?.checked || !record) { requireConsent(); return; }
    const accepted = record;
    const version = revision;
    button.disabled = true;
    try {
      await queue;
      const cart = await readCart();
      if (revision !== version || !checkbox.checked || fingerprint(cart) !== accepted.signature || !Object.entries(accepted.values).every(([key, value]) => cart.attributes?.[key] === value)) {
        await invalidate(true);
        requireConsent();
        return;
      }
      submitting = true;
      button.disabled = false;
      event.target.requestSubmit(button);
      submitting = false;
    } catch { await invalidate(true); message(true); }
  }, true);
  document.addEventListener('click', event => {
    if (event.target.closest('cart-remove-button, .quantity__button')) invalidate(true);
  }, true);
  document.addEventListener('change', event => {
    if (event.target.matches('[name="updates[]"]')) invalidate(true);
  }, true);
  document.addEventListener('variant:add', () => invalidate(true));
  document.addEventListener('cart:change', event => {
    const cart = event.detail?.cart || event.detail;
    if (record && Array.isArray(cart?.items) && fingerprint(cart) !== record.signature) invalidate(true);
  });
  document.addEventListener('cart:rendered', () => invalidate());
  subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
    if (['product-form', 'cart-items', 'cart-history-restore'].includes(event.source)) invalidate(true);
  });
  window.addEventListener('pageshow', event => { if (event.persisted) invalidate(true); });
  window.addEventListener('focus', async () => {
    if (!record) return;
    try {
      const cart = await readCart();
      if (fingerprint(cart) !== record.signature || cart.attributes?.[prefix + 'accepted'] !== 'yes') invalidate(true);
    } catch { invalidate(true); }
  });
  invalidate();
})();
